import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../../helpers';
import { ONBOARDING_QUESTIONNAIRES_API_BASE } from '../../constants';

import type { IOnboardingQuestionnaireInsurance } from '../../../../../pages/questionnaires/onboarding/components/forms/insurance/types';
import type {
  CreateInsuranceCardExtractionJobPayload,
  CreateInsuranceCardExtractionJobResponse,
  DeleteOnboardingQuestionnaireSecondaryInsurancePayload,
  GetInsuranceCardExtractionJobPayload,
  GetInsuranceCardExtractionJobResponse,
  SetOnboardingQuestionnaireNoSecondaryInsurancePayload,
  UpsertOnboardingQuestionnaireInsurancePayload,
} from './types';

export function createOnboardingInsuranceThunks() {
  return {
    upsertOnboardingQuestionnairePrimaryInsurance: createUpsertInsuranceThunk('primary'),
    upsertOnboardingQuestionnaireSecondaryInsurance: createUpsertInsuranceThunk('secondary'),
    deleteOnboardingQuestionnaireSecondaryInsurance:
      deleteOnboardingQuestionnaireSecondaryInsurance(),
    setOnboardingQuestionnaireNoSecondaryInsurance:
      setOnboardingQuestionnaireNoSecondaryInsurance(),
    createInsuranceCardExtractionJob: createInsuranceCardExtractionJob(),
    getInsuranceCardExtractionJob: getInsuranceCardExtractionJob(),
  };
}

function createUpsertInsuranceThunk(insuranceKind: 'primary' | 'secondary') {
  const actionSuffix = insuranceKind === 'primary' ? 'PrimaryInsurance' : 'SecondaryInsurance';
  return createAsyncThunk<
    IOnboardingQuestionnaireInsurance,
    UpsertOnboardingQuestionnaireInsurancePayload
  >(
    `onboardingQuestionnaire/upsertOnboardingQuestionnaire${actionSuffix}`,
    async ({ onboardingQuestionnaireId, data, getAccessTokenSilently }) => {
      const response: IOnboardingQuestionnaireInsurance = await fetchWrapper.patch(
        {
          url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/insurance/${insuranceKind}`,
          getAccessTokenSilently,
        },
        data,
      );
      return response;
    },
  );
}

function deleteOnboardingQuestionnaireSecondaryInsurance() {
  return createAsyncThunk<void, DeleteOnboardingQuestionnaireSecondaryInsurancePayload>(
    'onboardingQuestionnaire/deleteOnboardingQuestionnaireSecondaryInsurance',
    async ({ onboardingQuestionnaireId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/insurance/secondary`,
        getAccessTokenSilently,
      });
    },
  );
}

function setOnboardingQuestionnaireNoSecondaryInsurance() {
  return createAsyncThunk<void, SetOnboardingQuestionnaireNoSecondaryInsurancePayload>(
    'onboardingQuestionnaire/setOnboardingQuestionnaireNoSecondaryInsurance',
    async ({ onboardingQuestionnaireId, noSecondaryInsurance, getAccessTokenSilently }) => {
      await fetchWrapper.patch(
        {
          url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/insurance/preferences`,
          getAccessTokenSilently,
        },
        { no_secondary_insurance: noSecondaryInsurance },
      );
    },
  );
}

function createInsuranceCardExtractionJob() {
  return createAsyncThunk<
    CreateInsuranceCardExtractionJobResponse,
    CreateInsuranceCardExtractionJobPayload
  >(
    'onboardingQuestionnaire/createInsuranceCardExtractionJob',
    async ({ onboardingQuestionnaireId, insuranceType, getAccessTokenSilently }) => {
      const response: CreateInsuranceCardExtractionJobResponse = await fetchWrapper.post(
        {
          url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/insurance-card/extraction-jobs`,
          getAccessTokenSilently,
        },
        { insurance_type: insuranceType },
      );
      return response;
    },
  );
}

function getInsuranceCardExtractionJob() {
  return createAsyncThunk<
    GetInsuranceCardExtractionJobResponse,
    GetInsuranceCardExtractionJobPayload
  >(
    'onboardingQuestionnaire/getInsuranceCardExtractionJob',
    async ({ onboardingQuestionnaireId, jobId, getAccessTokenSilently }) => {
      const response: GetInsuranceCardExtractionJobResponse = await fetchWrapper.get({
        url: `${ONBOARDING_QUESTIONNAIRES_API_BASE}/${onboardingQuestionnaireId}/insurance-card/extraction-jobs/${jobId}`,
        getAccessTokenSilently,
      });
      return response;
    },
  );
}
